import { createState } from "src/utils/create_zustand_store";
import { formatPhoneNumber, formatUSPhoneNumber } from "src/utils/utils";
import { useApiRoutes } from "src/components/reusable/hooks/useApiRoutes";
import { useGlobalStore } from "src/components/reusable/hooks/useGlobalStore";

type TAdditionalDetailsState = {
  answer: string;
  questionId: number;
  isRequired: boolean;
  additionalInformation?: string;
  requiredFieldErrorMessage?: string;
  additionalFieldErrorMessage?: string;
};

export const useAdditionalDetailsStateState = createState({
  additionalDetailsState: [] as TAdditionalDetailsState[],
});

export function useAdditionalDetails() {
  const { additionalDetails } = useGlobalStore();
  const { insertAdditionalDetailsAnswersApi } = useApiRoutes();
  const { set, additionalDetailsState } = useAdditionalDetailsStateState();

  const alphanumericRegExp = /^[a-z0-9]+$/i;
  const emailRegExp =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  const numberRegExp = /^\d+$/;
  const phoneNumberRegExp = /^\d{10}$/;

  function addOption(currentAnswer: string, newOption: string) {
    return currentAnswer ? `${currentAnswer}, ${newOption}` : newOption;
  }

  function removeOption(currentAnswer: string, optionToRemove: string) {
    return currentAnswer
      .split(", ")
      .filter((item) => item !== optionToRemove)
      .join(", ");
  }

  // prettier-ignore
  function handleChange(answer: string, questionId: number, isRequired: boolean, additionalInformation?: string) {
    if (!additionalDetails) return;
    const additionalDetailsItem = additionalDetails.find((item) => item.id === questionId);
    if (additionalDetailsItem) {
      const filteredAdditionalDetailsState = additionalDetailsState.filter((item) => item.questionId !== questionId);
      if (filteredAdditionalDetailsState) {
        set({
          additionalDetailsState: [
            ...filteredAdditionalDetailsState,
            { ...additionalDetailsItem, answer, additionalInformation, isRequired, questionId: additionalDetailsItem.id },
          ],
        });
      }
    }
  }

  function handlePhoneNumberChange(event: any, questionId: number) {
    const additionalDetailsItem = additionalDetailsState.find(
      (item) => item.questionId === questionId
    );
    if (!additionalDetailsItem) return;

    let value = event.target.value;

    // fixing autofill issue
    if (
      event.inputType === "insertReplacementText" ||
      event.target.matches(":-webkit-autofill") ||
      (event.inputType == undefined && event.target.value.length >= 10)
    ) {
      const formattedNumber = formatUSPhoneNumber(value);
      set({
        additionalDetailsState: additionalDetailsState.map((item) =>
          item.questionId === questionId
            ? {
                ...item,
                additionalInformation: formattedNumber,
                additionalFieldErrorMessage: "",
              }
            : item
        ),
      });
      return;
    }

    const isNumber = /[0-9]/.test(event.nativeEvent.data);
    const isBackspace = event.nativeEvent.inputType === "deleteContentBackward";

    if (isBackspace) {
      set({
        additionalDetailsState: additionalDetailsState.map((item) =>
          item.questionId === questionId
            ? {
                ...item,
                additionalInformation: value,
                additionalFieldErrorMessage: "",
              }
            : item
        ),
      });
    } else if (isNumber && value.length <= 12) {
      set({
        additionalDetailsState: additionalDetailsState.map((item) =>
          item.questionId === questionId
            ? {
                ...item,
                additionalInformation: formatPhoneNumber(value),
                additionalFieldErrorMessage: "",
              }
            : item
        ),
      });
    }
  }

  // prettier-ignore
  function handleCheckAdditionalDetailsRequired(): boolean {
    if (!additionalDetails?.length) return true;

    let isValid = true;
    const updatedState = [...additionalDetailsState];

    for (const question of additionalDetails) {
      const item = additionalDetailsState.find((item) => item.questionId === question.id);
      
      // Check if required question is answered
      if (question.isRequired) {
        if (!item || !item.answer) {
          const newItem = {
            ...(item || question),
            requiredFieldErrorMessage: "This field is required",
            answer: "",
            questionId: question.id
          };
          
          const existingIndex = updatedState.findIndex(stateItem => stateItem.questionId === question.id);
          if (existingIndex >= 0) {
            updatedState[existingIndex] = newItem;
          } else {
            updatedState.push(newItem);
          }
          isValid = false;
          continue;
        }
      }

      // Check if additional information is required when "other" is selected
      if (item?.answer?.toLowerCase().includes("other") && !item?.additionalInformation) {
        const existingIndex = updatedState.findIndex(stateItem => stateItem.questionId === question.id);
        if (existingIndex >= 0) {
          updatedState[existingIndex] = {
            ...updatedState[existingIndex],
            additionalFieldErrorMessage: "Additional information is required when 'other' is selected"
          };
        }
        isValid = false;
        continue;
      }

      // Validate additional information if present
      const additionalInformation = item?.additionalInformation;
      if (!!additionalInformation) {
        let hasError = false;
        let errorMessage = "";

        // checking if email is valid
        if (question.formatRestrictions === "email" && !emailRegExp.test(additionalInformation)) {
          hasError = true;
        }

        // checking if alphanumeric is valid
        if (question.formatRestrictions === "alphanumeric" && !alphanumericRegExp.test(additionalInformation)) {
          hasError = true;
        }

        // checking if number is valid
        if (question.formatRestrictions === "number" && !numberRegExp.test(additionalInformation)) {
          hasError = true;
          errorMessage = "Please enter a valid number";
        }

        // checking if phone number is valid
        if (question.formatRestrictions === "phone" && !phoneNumberRegExp.test(additionalInformation.replaceAll("-", ""))) {
          hasError = true;
          errorMessage = "Please enter a valid phone number";
        }

        if (hasError) {
          const existingIndex = updatedState.findIndex(stateItem => stateItem.questionId === question.id);
          if (existingIndex >= 0) {
            updatedState[existingIndex] = {
              ...updatedState[existingIndex],
              additionalFieldErrorMessage: errorMessage
            };
          }
          isValid = false;
        }
      }
    }

    if (!isValid) {
      set({ additionalDetailsState: updatedState });
    }

    return isValid;
  }

  async function handleSubmitAdditionalDetails() {
    try {
      if (!additionalDetailsState?.length) return;
      const additionalDetailsAnswers = additionalDetailsState.map((item) => ({
        questionId: item.questionId,
        answer: item.answer,
        additionalInformation: item.additionalInformation,
      }));
      await insertAdditionalDetailsAnswersApi.post({
        additionalDetailsAnswers,
      });
    } catch (error) {
      console.log(error);
    }
  }

  return {
    addOption,
    emailRegExp,
    numberRegExp,
    removeOption,
    handleChange,
    phoneNumberRegExp,
    alphanumericRegExp,
    additionalDetailsState,
    handlePhoneNumberChange,
    handleSubmitAdditionalDetails,
    handleCheckAdditionalDetailsRequired,
  };
}
